import React, { useCallback, useEffect } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  RefreshCw,
  Search,
  Target,
  XCircle,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../slices';
import {
  fetchStarlineSessions,
  setStarlineFilters,
} from '../../slices/starlineMarket/starlineMarketSlice';

const StarlineMarket: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    sessions,
    count,
    loading,
    error,
    filters,
  } = useSelector((state: RootState) => state.StarlineMarket);

  const loadSessions = useCallback(() => {
    dispatch(
      fetchStarlineSessions({
        marketId: filters.marketId,
        sessionDate: filters.sessionDate,
        status: filters.status,
        openOnly: filters.openOnly,
      })
    );
  }, [
    dispatch,
    filters.marketId,
    filters.sessionDate,
    filters.status,
    filters.openOnly,
  ]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const formatTime = (value?: string | null): string => {
    if (!value) {
      return '-';
    }

    const timePart = value.includes('T')
      ? value.split('T')[1]
      : value;

    const cleanTime = timePart.replace('Z', '').slice(0, 5);
    const [hourString, minute = '00'] = cleanTime.split(':');
    const hour = Number(hourString);

    if (Number.isNaN(hour)) {
      return value;
    }

    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minute} ${suffix}`;
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  const formatStatusLabel = (status?: string): string => {
    if (!status) {
      return '-';
    }

    return status
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const getStatusClass = (status?: string): string => {
    const normalized = String(status || '').toLowerCase();

    if (normalized === 'open') {
      return 'bg-green-100 text-green-700';
    }

    if (normalized === 'scheduled') {
      return 'bg-blue-100 text-blue-700';
    }

    if (normalized === 'result_declared') {
      return 'bg-violet-100 text-violet-700';
    }

    if (normalized === 'settled') {
      return 'bg-slate-200 text-slate-700';
    }

    if (normalized === 'cancelled') {
      return 'bg-red-100 text-red-700';
    }

    if (normalized === 'closed') {
      return 'bg-amber-100 text-amber-700';
    }

    return 'bg-slate-100 text-slate-700';
  };

  const openCount = sessions.filter(
    (session) => session.is_betting_open
  ).length;

  const declaredCount = sessions.filter(
    (session) =>
      Boolean(session.result_pana) ||
      Boolean(session.result_digit) ||
      Boolean(session.result_declared_at)
  ).length;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Starline Game List
          </h1>

          <p className="mt-1 text-sm text-slate-600">
            View and monitor Starline sessions by date and status.
          </p>
        </div>

        <button
          type="button"
          onClick={loadSessions}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
          />

          Refresh
        </button>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
              <Target className="h-5 w-5" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Total Sessions
              </p>

              <p className="text-2xl font-bold text-slate-900">
                {count}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Betting Open
              </p>

              <p className="text-2xl font-bold text-slate-900">
                {openCount}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-violet-100 p-2 text-violet-700">
              <CalendarDays className="h-5 w-5" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Result Declared
              </p>

              <p className="text-2xl font-bold text-slate-900">
                {declaredCount}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-100 p-2 text-slate-700">
              <Clock3 className="h-5 w-5" />
            </div>

            <div>
              <p className="text-sm text-slate-500">
                Selected Date
              </p>

              <p className="text-base font-semibold text-slate-900">
                {filters.sessionDate || '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label
              htmlFor="starline-session-date"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Session Date
            </label>

            <input
              id="starline-session-date"
              type="date"
              value={filters.sessionDate}
              onChange={(event) =>
                dispatch(
                  setStarlineFilters({
                    sessionDate: event.target.value,
                  })
                )
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="starline-status"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Status
            </label>

            <select
              id="starline-status"
              value={filters.status}
              onChange={(event) =>
                dispatch(
                  setStarlineFilters({
                    status: event.target.value,
                  })
                )
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="result_declared">
                Result Declared
              </option>
              <option value="settled">Settled</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="starline-market-id"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Market ID
            </label>

            <input
              id="starline-market-id"
              type="number"
              min="1"
              value={filters.marketId}
              onChange={(event) =>
                dispatch(
                  setStarlineFilters({
                    marketId: Number(event.target.value || 33),
                  })
                )
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="flex items-end">
            <label className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-slate-300 px-3 py-2">
              <input
                type="checkbox"
                checked={filters.openOnly}
                onChange={(event) =>
                  dispatch(
                    setStarlineFilters({
                      openOnly: event.target.checked,
                    })
                  )
                }
                className="h-4 w-4"
              />

              <span className="text-sm font-medium text-slate-700">
                Show open sessions only
              </span>
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <XCircle className="mt-0.5 h-5 w-5 flex-none" />

          <div>
            <p className="font-medium">
              Unable to load Starline sessions
            </p>

            <p className="mt-1 text-sm">
              {error}
            </p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-500" />

            <h2 className="font-semibold text-slate-900">
              Starline Sessions
            </h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {[
                  'Market',
                  'Date',
                  'Session Time',
                  'Status',
                  'Result',
                  'Betting',
                  'Declared At',
                  'Settled At',
                ].map((heading) => (
                  <th
                    key={heading}
                    className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 bg-white">
              {loading && sessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    <RefreshCw className="mx-auto mb-3 h-6 w-6 animate-spin" />

                    Loading Starline sessions...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    No Starline sessions found for the selected filters.
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr
                    key={session.id}
                    className="hover:bg-slate-50"
                  >

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                      {session.market_name || session.market}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                      {session.session_date || '-'}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900">
                      {formatTime(session.session_time)}
                    </td>



                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClass(
                          session.status
                        )}`}
                      >
                        {formatStatusLabel(session.status)}
                      </span>
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900">
                      {session.result_display ||
                        [session.result_pana, session.result_digit]
                          .filter(Boolean)
                          .join('-') ||
                        '-'}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          session.is_betting_open
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {session.is_betting_open ? 'Open' : 'Closed'}
                      </span>
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                      {formatDateTime(session.result_declared_at)}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                      {formatDateTime(session.settled_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StarlineMarket;